import React from 'react';
import FiltersLimitedToOneRow from 'components/search_page/filters_limited_to_one_row';
import CurrentFilters from 'components/search_page/current_filters';
import PageInfo from 'components/search_page/page_info';
import PropTypes from 'prop-types';
import Tag from '../../tags/tag';

export default function ReportFilters({
  behaviorToggle,
  filterMapping,
  filters,
  pageData,
  reportType,
  searchText,
  onClearFilters,
  onRemoveFilter,
  onSearchChange,
  onToggleAllFiltersModal
}) {
  function generateMultiSelectTags(key) {
    const filtersForKey = filters[key];
    const filtersSelected = filterMapping[key].options.filter(option => filtersForKey.includes(option.value));

    if (filterMapping[key].label) {
      return filtersSelected.map(filter => (
        <Tag
          key={`${key}-${filter.value}`}
          removable
          onRemove={() => onRemoveFilter(key, filter.value)}
        >
          {filterMapping[key].label} - {filter.label}
        </Tag>
      ));
    } else {
      return filtersSelected.map(filter => (
        <Tag
          key={`${key}-${filter.value}`}
          removable
          onRemove={() => onRemoveFilter(key, filter.value)}
        >
          {filter.label}
        </Tag>
      ));
    }
  }

  function generateAccordionTags(key) {
    const tags = [];
    const filtersForKey = filters[key];

    const filtersSelected = filterMapping[key].options.flat().filter(
      option => (filtersForKey.includes(option.value))
    );

    filtersSelected.map(filter => (
      tags.push(
        <Tag
          key={`${key}-${filter.value}`}
          removable
          onRemove={() => onRemoveFilter(key, filter.value)}
        >
          {filterMapping[key].label} - {filter.label}
        </Tag>
      )
    ));

    return tags;
  }

  function generateMinMaxTags(key, isDate) {
    const tags = [];
    const filtersForKey = filters[key];

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short', // e.g., "Jan"
        day: '2-digit',
      }).format(date);
    };

    const renderTag = (minOrMax, minMaxLabel) => (
      <Tag
        key={`${key}-${minOrMax}`}
        removable
        onRemove={() => onRemoveFilter(key, minOrMax)}
      >
        {filterMapping[key].label} {minMaxLabel}{' '}
        {isDate ? formatDate(filtersForKey[minOrMax]) : filtersForKey[minOrMax]}
      </Tag>
    );

    if (filtersForKey.min !== '') tags.push(renderTag('min', '>='));
    if (filtersForKey.max !== '') tags.push(renderTag('max', '<='));

    return tags;
  }

  function generateSearchTag(key) {
    const value = filters[key];

    if (!value) return [];

    return [(
      <Tag
        key={`${key}-${value}`}
        removable
        onRemove={onSearchChange}
      >
        {filterMapping[key].label} - {value}
      </Tag>
    )];
  }

  function generateChildFilterTag(filterData, parentKey) {
    const childFilter = filterData.childFilter;
    const filterValue = filters[childFilter.key];
    if (!filterValue) return [];

    return [(
      <Tag
        key={childFilter.key}
        removable
        onRemove={() => onRemoveFilter(parentKey, '', true)}
      >
        {childFilter.label}
      </Tag>
    )];
  }

  function renderTags() {
    const tags = [];

    Object.keys(filterMapping).forEach((key) => {
      const filterData = filterMapping[key];
      const filterType = filterData.type;

      if (filterType === 'range') {
        tags.push(...generateMinMaxTags(key, false));
      } else if (filterType === 'multiselect' || filterType === 'dropdown') {
        tags.push(...generateMultiSelectTags(key));
      } else if (filterType === 'accordion') {
        tags.push(...generateAccordionTags(key, filterData));
      } else if (filterType === 'date') {
        tags.push(...generateMinMaxTags(key, true));
      } else if (filterType === 'search') {
        tags.push(...generateSearchTag(key, true));
      }

      if (filterData.childFilter) {
        tags.push(...generateChildFilterTag(filterData, key));
      }
    });

    return tags;
  }

  function filterMappingToShow() {
    return Object.keys(filterMapping).reduce((acc, filterKey) => {
      if (filterMapping[filterKey].isDefault) acc[filterKey] = filterMapping[filterKey];

      return acc;
    }, {});
  }

  function determinePlaceholder(reportType) {
    switch (reportType) {
      case 'user':
        return 'Search by name, company or office';
      default:
        return 'Search by name, address, city, or zip';
    }
  }

  const currentFilterTags = renderTags();

  return (
    <div className="d-flex flex-column gap-2 mb-2">
      <FiltersLimitedToOneRow
        behaviorToggle={behaviorToggle}
        filterMapping={filterMappingToShow()}
        handleSearchChange={onSearchChange}
        searchPlaceholder={determinePlaceholder(reportType)}
        searchText={searchText}
        showAllFiltersButton={reportType !== 'user'}
        toggleAllFiltersModal={onToggleAllFiltersModal}
        {...filters}
      />
      {currentFilterTags.length > 0 && (
        <div className="d-flex justify-content-between align-items-end">
          <CurrentFilters onClearFilters={onClearFilters}>
            {renderTags()}
          </CurrentFilters>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-end">
        <div className="text-muted">
          {pageData.propertySelectedCount > 0 ? `${pageData.propertySelectedCount} selected` : ''}
        </div>
        <PageInfo {...pageData}/>
      </div>
    </div>
  );
}

ReportFilters.propTypes = {
  behaviorToggle: PropTypes.shape({
    value: PropTypes.any,
    label: PropTypes.string,
    checked: PropTypes.bool,
    onChange: PropTypes.func,
    optionToToggle: PropTypes.string
  }),
  filterMapping: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  pageData: PropTypes.object.isRequired,
  reportType: PropTypes.string.isRequired,
  searchText: PropTypes.string,
  onClearFilters: PropTypes.func.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onToggleAllFiltersModal: PropTypes.func.isRequired,
};
